import { useState, useEffect } from 'react';
import { Copy, Check, Sparkles, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Shown after an influencer (demo invite) publishes their first message.
 * Displays their referral link so they can share with their audience immediately.
 */
export function InfluencerPostPublishCard() {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('referral_code')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) setReferralCode((data as any).referral_code);
      });
  }, [user]);

  const referralLink = referralCode
    ? `https://say-it-now.app/?ref=${referralCode}`
    : '';

  const handleCopy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = referralLink;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2500);
  };

  if (!referralCode) return null;

  return (
    <Card className="border-primary/20 bg-primary/5 rounded-2xl overflow-hidden">
      <CardContent className="p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="text-base font-bold text-foreground">
            Ready to share this with your audience?
          </h3>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">
          Share your personal referral link — when your followers sign up and create messages, you'll see the impact in real time.
        </p>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Your referral link
          </label>
          <div className="flex gap-2">
            <Input
              readOnly
              value={referralLink}
              className="rounded-xl bg-background text-xs font-mono h-10"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <Button
              size="sm"
              className="rounded-xl shrink-0 h-10 gap-1.5 px-4"
              onClick={handleCopy}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground/70 italic">
          Tip: Add this link to your bio, story, or video description.
        </p>
      </CardContent>
    </Card>
  );
}
